/**
 * Pure decisions behind the edge-swipe-back gesture, kept free of the DOM so
 * they can be unit tested. See `install-swipe-back.ts` for how they are used.
 */

/** Touches that start further than this from the left edge are never ours. */
export const SWIPE_BACK_EDGE_PX = 24;

/** Movement before the gesture decides whether it is a back swipe. */
export const SWIPE_BACK_CLAIM_PX = 10;

/**
 * A press that sits still this long before moving is a long-press (text
 * selection, context menu), not the start of a swipe.
 */
export const SWIPE_BACK_LONG_PRESS_MS = 400;

/** Fraction of the screen past which a release commits, and the haptic arms. */
export const SWIPE_BACK_COMMIT_PROGRESS = 0.4;

/** px/ms. A rightward flick at least this fast commits from any distance. */
export const SWIPE_BACK_FLICK_VELOCITY = 0.45;

/** px/ms. A leftward flick at least this fast cancels from any distance. */
export const SWIPE_BACK_CANCEL_VELOCITY = -0.3;

/** A flick still has to have travelled this far, so a twitch never navigates. */
export const SWIPE_BACK_MIN_FLICK_PX = 32;

export type SwipeIntent = "undecided" | "back" | "other";

/**
 * Decide what a press that started in the edge zone is doing.
 *
 * "back" needs a clearly rightward, mostly horizontal move — the same 1.2:1
 * bias UIKit's edge pan uses — so a slightly diagonal scroll that happens to
 * start at the edge still scrolls. Anything else ("other") hands the touch
 * back to the page for good.
 */
export function classifySwipeIntent(
  dx: number,
  dy: number,
  claimPx = SWIPE_BACK_CLAIM_PX,
): SwipeIntent {
  const absX = Math.abs(dx);
  const absY = Math.abs(dy);
  if (Math.max(absX, absY) < claimPx) return "undecided";
  if (dx > 0 && absX > absY * 1.2) return "back";
  return "other";
}

/**
 * Whether a release at `dx` (px dragged) moving at `velocity` (px/ms, positive
 * = rightward) should complete the back navigation.
 */
export function shouldCommitSwipeBack({
  dx,
  width,
  velocity,
}: {
  dx: number;
  width: number;
  velocity: number;
}): boolean {
  if (width <= 0 || dx <= 0) return false;
  if (velocity <= SWIPE_BACK_CANCEL_VELOCITY) return false;
  if (velocity >= SWIPE_BACK_FLICK_VELOCITY && dx >= SWIPE_BACK_MIN_FLICK_PX)
    return true;
  return dx / width >= SWIPE_BACK_COMMIT_PROGRESS;
}

/**
 * Release velocity in px/ms from recent `{ t, x }` samples (oldest first),
 * looking back at most `windowMs` so a pause before lifting reads as zero.
 */
export function releaseVelocity(
  samples: ReadonlyArray<{ t: number; x: number }>,
  windowMs = 80,
): number {
  const last = samples.at(-1);
  if (!last) return 0;
  let first = last;
  for (let index = samples.length - 1; index >= 0; index -= 1) {
    const sample = samples[index];
    if (!sample || last.t - sample.t > windowMs) break;
    first = sample;
  }
  const elapsed = last.t - first.t;
  return elapsed > 0 ? (last.x - first.x) / elapsed : 0;
}

/**
 * Compose a horizontal offset onto an element's existing CSS `translate`.
 *
 * Tailwind v4 positions with the individual `translate` property
 * (`-translate-x-1/2` and friends), and vaul drives its sheets with
 * `transform`. Writing a bare `translate: Npx 0` would wipe the former, so
 * the offset is added to whatever `getComputedStyle().translate` reported.
 */
export function composeTranslate(computed: string, offsetPx: number): string {
  const offset = `${Math.round(offsetPx * 100) / 100}px`;
  const value = computed.trim();
  if (!value || value === "none") return `${offset} 0px`;
  const [x = "0px", y = "0px", z] = splitTopLevel(value);
  const composedX = `calc(${x} + ${offset})`;
  return z ? `${composedX} ${y} ${z}` : `${composedX} ${y}`;
}

/** Split on whitespace outside parentheses, so `calc(a + b)` stays whole. */
function splitTopLevel(value: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let current = "";
  for (const char of value) {
    if (char === "(") depth += 1;
    else if (char === ")") depth = Math.max(0, depth - 1);
    if (depth === 0 && /\s/.test(char)) {
      if (current) parts.push(current);
      current = "";
      continue;
    }
    current += char;
  }
  if (current) parts.push(current);
  return parts;
}
